/* eslint-disable import/prefer-default-export */
import { useState, useCallback, useEffect } from 'react'
import { getAllChildValues } from '../utils/dropdownUtils'

/**
 * Custom hook for managing dropdown state and interactions
 * @param {Object} params - Hook parameters
 * @param {Array} params.options - Array of option objects
 * @param {Array} params.initialValues - Initial selected values
 * @param {Function} params.onApply - Callback when Apply is clicked
 * @param {Function} params.onClear - Callback when Clear is clicked
 * @param {Map} params.optionsMap - Map of all options for quick lookup
 * @returns {Object} State and handlers for dropdown
 */
export const useDropdownState = ({ initialValues = [], onApply, onClear, optionsMap }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [viewSelectedMode, setViewSelectedMode] = useState(false)
  const [collapsed, setCollapsed] = useState(new Set())
  const [selectedValues, setSelectedValues] = useState(initialValues)

  // Update selected values when prop changes
  useEffect(() => {
    if (initialValues.length > 0) {
      setSelectedValues(initialValues)
    }
  }, [initialValues])

  useEffect(() => {
    if (search === '') {
      setCollapsed(new Set())
    }
  }, [search])

  // Toggle collapse state for parent options
  const toggleCollapse = useCallback((value) => {
    setCollapsed((prev) => {
      const newSet = new Set(prev)
      newSet.has(value) ? newSet.delete(value) : newSet.add(value)
      return newSet
    })
  }, [])

  // Toggle selection state for options
  const toggleValue = useCallback(
    (value) => {
      const option = optionsMap.get(value)
      if (!option) return

      const valuesToToggle = getAllChildValues(option)
      const isSelected = valuesToToggle.every((v) => selectedValues.includes(v))

      if (isSelected) {
        setSelectedValues((prev) => prev.filter((v) => !valuesToToggle.includes(v)))
      } else {
        setSelectedValues((prev) => [...new Set([...prev, ...valuesToToggle])])
      }
    },
    [optionsMap, selectedValues]
  )

  // Handle Apply button click
  const handleApply = useCallback(() => {
    setIsOpen(false)
    onApply?.(selectedValues)
  }, [selectedValues, onApply])

  // Handle Clear button click
  const handleClear = useCallback(() => {
    setSelectedValues([])
    setIsOpen(false)
    onClear?.()
  }, [onClear])

  // Handle View Selected toggle
  const handleViewSelected = useCallback(() => {
    if (viewSelectedMode) {
      setSelectedValues([])
      setViewSelectedMode(false)
    } else {
      setViewSelectedMode(true)
    }
  }, [viewSelectedMode])

  // Close dropdown
  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Open dropdown
  const openDropdown = useCallback(() => {
    setIsOpen(true)
  }, [])

  return {
    // State
    isOpen,
    search,
    viewSelectedMode,
    collapsed,
    selectedValues,
    setCollapsed,

    // Setters
    setSearch,

    // Handlers
    toggleCollapse,
    toggleValue,
    handleApply,
    handleClear,
    handleViewSelected,
    closeDropdown,
    openDropdown,
  }
}
